import React, { useEffect, useState } from 'react'
import App from 'next/app'
import { DefaultSeo } from 'next-seo'
import NProgress from '~/components/NProgress'
import { useRouter } from 'next/router'
import { ToastProvider } from '@ticketswap/solar'
import BaseStyles from '~/components/BaseStyles'
import { setTrackingIdentity, page, pageSource } from '~/utils'
import ViewerProvider from '~/components/ViewerContext'
import ConsentDialog from '~/components/ConsentDialog/dynamic'
import NetworkStatus from '~/components/NetworkStatus'
import createSeoConfig from '~/lib/next-seo-defaults'
import { withTranslation, appWithTranslation } from '~/i18n'
import { withServerResponse } from '~/components/ServerResponseContext'
import getConfig from 'next/config'
import LocaleBar from '~/components/LocaleBar/dynamic'
import getViewer from '~/lib/get-viewer'
import { NativeBridge } from '~/utils/nativeBridge'
import { LanguageProvider } from '~/components/LanguageContext'
import getLanguageFromHostname from '~/utils/i18n/getLanguageFromHostname'
import getLocaleFromHostname from '~/utils/i18n/getLocaleFromHostname'
import getDeviceInfo from '~/utils/getDeviceInfo'
import { decodeGlobalId } from '~/utils/id'
import { withApollo } from '~/lib/apollo'
import VoucherToast from '~/components/VoucherToast/dynamic'
import CookieToast from '~/components/CookieToast/dynamic'
import { LocaleProvider } from '~/components/LocaleContext'
import { IntercomProvider } from '~/components/IntercomProvider'
import FacebookPixelProvider from '~/components/Pixel'
import FacebookProvider from '~/components/FacebookContext'

const {
  FACEBOOK_APP_ID,
  USER_ID_COOKIE_NAME,
  INTERCOM_APP_ID,
  FACEBOOK_PIXEL_ID,
} = getConfig().publicRuntimeConfig

const MyApp = ({
  Component,
  viewer,
  deviceInfo,
  pageProps,
  t,
  localeFromRequest,
  languageFromRequest,
  i18n,
  openGraphUrl,
  currentUrl,
}) => {
  const seoDescription = t('common:site.description')
  const languageLocale = languageFromRequest || i18n.language || 'en'
  const language = languageLocale.split('-')[0]
  const router = useRouter()
  const [tracked, setTracked] = useState(false)

  useEffect(() => {
    const handleRouteChange = path => page(path)

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    if (!tracked) {
      setTracked(true)
      viewer && setTrackingIdentity(viewer, false)

      // Track initial page-view
      page(router.asPath)

      // Track referrer-info once per session
      pageSource(router.asPath)
    }
  }, [router.asPath, tracked, viewer])

  return (
    <LanguageProvider value={language}>
      <LocaleProvider value={localeFromRequest}>
        <NProgress />
        <BaseStyles />
        <NativeBridge />
        <DefaultSeo {...createSeoConfig(seoDescription, openGraphUrl)} />
        <FacebookProvider appId={FACEBOOK_APP_ID}>
          <FacebookPixelProvider pixelId={FACEBOOK_PIXEL_ID}>
            <ViewerProvider data={{ viewer, deviceInfo, currentUrl }}>
              <IntercomProvider appId={INTERCOM_APP_ID}>
                <ToastProvider>
                  <VoucherToast t={t} />
                  <ConsentDialog />
                  <LocaleBar language={language} />
                  <NetworkStatus />
                  <Component {...pageProps} />
                  {!deviceInfo.isApp && <CookieToast t={t} />}
                </ToastProvider>
              </IntercomProvider>
            </ViewerProvider>
          </FacebookPixelProvider>
        </FacebookProvider>
      </LocaleProvider>
    </LanguageProvider>
  )
}

MyApp.getInitialProps = async appContext => {
  const { ctx } = appContext
  const appProps = await App.getInitialProps(appContext)

  const deviceInfo = getDeviceInfo(ctx.req) || {}
  const viewer = await getViewer(ctx.apolloClient, ctx.req)

  if (viewer && ctx.res) {
    ctx.res.cookie(USER_ID_COOKIE_NAME, decodeGlobalId(viewer.id), {
      secure: true,
      sameSite: 'lax',
    })
  }

  const currentUrl = ctx?.req
    ? `${ctx.req.headers['x-forwarded-proto']}://${ctx.req.get('host')}`
    : `${window.location.protocol}//${window.location.host}`
  const openGraphUrl = ctx?.req
    ? `${currentUrl}${ctx.req.originalUrl}`
    : `${currentUrl}${window.location.pathname}`

  // `pageProps` will get serialized automatically by NextJs
  return {
    viewer,
    deviceInfo,
    localeFromRequest: ctx?.req
      ? getLocaleFromHostname(ctx.req.headers.host)
      : getLocaleFromHostname(window.location.hostname),
    languageFromRequest: ctx?.req ? getLanguageFromHostname(ctx.req.headers.host) : null,
    openGraphUrl,
    currentUrl,
    ...appProps,
  }
}

export default withApollo({ ssr: true })(
  appWithTranslation(withServerResponse(withTranslation(['common'])(MyApp)))
)
